// Boot orchestrator — Task 4.12.
//
// Resolves the shareId from the URL, fetches manifest+peaks, instantiates the
// Player, wires its events into the shared state store, and mounts every UI
// panel into its `#share-*` host. Renders an error screen on invalid path,
// 404, or load failure. Auto-boots on page load unless the `__SHARE_WEB_TEST__`
// global is set (tests import this module and drive `boot()` themselves).

// Style imports. Cascade order matters — desktop CSS comes FIRST (provides the
// source-of-truth selectors for .app/.rh-*/.tr-*/.brand/.tb-*/.sl-*), then web
// CSS LAYERS overrides on top. Web layout.css overrides .app grid template +
// min-width:0 (desktop sets 1180px), web web-only-overrides adds web-specific
// modifiers, etc. Tokens.css MUST be loaded first so CSS custom props are
// available to all subsequent rules.
import './styles/tokens.css';

// Desktop CSS — source of truth (still reused via @desktop-css alias).
import '@desktop-css/rehearsal/player-panel-shell.css';
import '@desktop-css/rehearsal/controls-column.css';
import '@desktop-css/rehearsal/waves-column.css';
import '@desktop-css/rehearsal/takes-sidebar.css';
import '@desktop-css/app-shell.css';
import '@desktop-css/topbar.css';
// Vendored desktop CSS — these three legacy files were deleted on desktop during
// the design-system migration (song-header → bw-song-header `3a47150`, transport →
// bw-transport `d228e60`, setlist-panel `0374b6c`). share-web still renders the
// plain desktop classes, so we vendor the pre-deletion CSS locally to decouple
// from the volatile desktop paths (full CSS decoupling / DS adoption = sub-project 4).
import './styles/desktop/song-header.css';
import './styles/desktop/transport.css';
import './styles/desktop/setlist-panel.css';

// Web overrides — must come AFTER desktop CSS to win cascade.
import './styles/layout.css';
import './styles/topbar.css';
import './styles/promo-footer.css';
import './styles/promo-modal.css';
import './styles/web-only-overrides.css';
import './styles/setlist-sidebar.css';
import './styles/share-modal.css';
import './styles/sections.css';
import './styles/library.css';
import './styles/app-header.css';
import './styles/dashboard.css';

import { parseShareIdFromPath, isLibraryPath, isLoginPath, isDashboardPath, parseInviteToken, parseTextShareId, isLiveWordsEntry, parseSongWordsId } from './router.js';
import { signOut } from './auth.js';
import { fetchMe } from './me.js';
import { roleHome } from './role-home.js';
import { fetchLibrary } from './library.js';
import { createOfflineStore } from './offline/offline-store.js';
import { idbBackend } from './offline/idb-backend.js';
import { memoryBackend } from './offline/kv-backend.js';
import { syncAction } from './offline/library-sync.js';
import { applySyncAction } from './offline/apply-sync.js';
import { freshness } from './offline/freshness.js';
import { prefetchWords } from './offline/prefetch.js';
import { runSetlistPrefetch } from './offline/setlist-prefetch-wiring.js';
import { resolveWordsForPage } from './offline/words-resolve.js';
import { OFFLINE_UID_KEY, readOfflineUserId, openOfflineStore, eraseOfflineData } from './offline/open-store.js';
import { mountLibraryView } from './ui/library-view.js';
import { backfillSongTexts } from './song-texts-backfill-api.js';
import { fetchSongText, setSongTextVisibility, removeSongText } from './song-text-api.js';
import { fetchDashboard } from './dashboard.js';
import { mountDashboardView, inviteMsgFor } from './ui/dashboard-view.js';
import { joinTeam, leaveTeam, acceptInvite, previewInvite, rotateCode, invite } from './teams-api.js';
import { listViewers, revokeViewer as apiRevokeViewer, setVisibility as apiSetVisibility, listMyShares, removeShare } from './shares-api.js';
import { fetchPublishedSetlist } from './setlist.js';
import { enableIosSilentPlayback } from './ios-audio-session.js';
import { bootUnifiedSetlistRoute } from './setlist-screen/boot.js';
import { legacyLinkRedirectPath } from './setlist-screen/text-link-redirect.js';
import {
  loadManifest,
  loadPeaks,
  loadPerTrackPeaks,
} from './manifest-loader.js';
import { mountLoginGate } from './ui/login-gate.js';
import { mountInviteView } from './ui/invite-view.js';
import { t, getLocale } from './ui/i18n.js';
import { createPlayer, type Player } from './player.js';
import { createBackgroundAudio } from './audio/background-audio.js';
import { renderMixToBlob } from './audio/mix-renderer.js';
import { wireMediaSession, setPositionState, setPlaybackState } from './audio/media-session.js';
import { wireBackgroundPlayback } from './audio/background-orchestrator.js';
import { getState, setState, subscribe } from './state.js';
import { mountAppHeader, type AppHeaderHandle } from './ui/app-header.js';
import { openFeedbackModal } from './ui/feedback-modal.js';
import { initPosthog, identifyOwner } from './posthog-init.js';
import './styles/feedback-modal.css';
import { mountPromoFooter } from './ui/promo-footer.js';
import { mountPromoModal } from './ui/promo-modal.js';
import { mountSetlistRail } from './player-view/setlist-rail.js';
import { mountPlayerView } from './player-view/mount.js';
import { wireSetlistPlayer, type SetlistPlayerWiring } from './player-view/setlist-player-wiring.js';
import type { SummaryPeaks, PerTrackSummaryPeaks } from '@bandway/share-types';
import { mountShareModal } from './ui/share-modal.js';
import { registerServiceWorker } from './sw-register.js';
import { applyBootTheme } from './theme-boot.js';
import { requestPersistentStorage } from './storage-persist.js';
import { fetchTextShare } from './text-share/text-share-api.js';
import { createContentSync } from './text-share/content-sync.js';
import { mountWordsView, type WordsViewHandle } from './text-share/words-view.js';
import { mountSongWordsView, type SongWordsViewHandle } from './song-words-view.js';
import { wordsSongs, wordsSectionsOf, wordsDurationSecOf } from './text-share/words-model.js';
import { createLiveClient, type LiveClient } from './live/live-client.js';
import {
  renderTextShareEnded,
  renderTextShareNotFound,
  renderTextShareError,
} from './text-share/words-status.js';

// Лендинг — единственный выход со страницы слов: логина у её читателя нет, и
// «назад в библиотеку» ему предложить нечего. Литерал module-local, как у
// четырёх соседей (ui/promo-footer.ts, ui/app-header.ts, promo-content.ts,
// ui/login-gate.ts).
const LANDING_URL = 'https://bandway.app';

// In production the CDN serves `/shares/<shareId>/...`. On localhost (both
// `vite dev` and `vite preview`) the dev-fixtures plugin (see `vite.config.ts`)
// mirrors that layout under `/dev-fixtures` so the SPA can be driven locally
// without touching the live CDN. The acceptance procedure uses `npm run preview`
// to validate SW caching against the production build output — hostname-based
// detection ensures preview mode also picks up local fixtures.
// See `apps/share-web/README.md`.
const DEFAULT_CDN_BASE = (() => {
  if (typeof location !== 'undefined') {
    const host = location.hostname;
    if (host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0') {
      return `${location.origin}/dev-fixtures`;
    }
  }
  return 'https://cdn.bandway.app';
})();

// Component 1 (web light playback): create the real playback AudioContext at a
// reduced 24 kHz rather than the device default (typically 48 kHz). The context
// rate drives `decodeAudioData`, so every stem resamples to 24 kHz on decode →
// decoded Float32 PCM is halved, keeping multitrack shares under the mobile
// tab-memory limit. Scheduling stays in the time domain (seconds), so
// sample-accuracy is unchanged; only playback bandwidth drops to ≤12 kHz
// (acceptable for a rehearsal reference mix). See the 2026-06-22 design spec.
export const WEB_PLAYBACK_SAMPLE_RATE = 24000;

/**
 * Construct the production playback AudioContext at {@link WEB_PLAYBACK_SAMPLE_RATE}.
 * `{ sampleRate }` is honored on iOS Safari 14.5+ and current Chrome/Firefox;
 * older Safari throws on the option, so we fall back to a default-rate context.
 * Uses the global `AudioContext` so tests can stub it via `vi.stubGlobal`.
 */
export function createPlaybackContext(): AudioContext {
  try {
    return new AudioContext({ sampleRate: WEB_PLAYBACK_SAMPLE_RATE });
  } catch {
    return new AudioContext();
  }
}

export interface BootOpts {
  cdnBase?: string;
  audioContextFactory?: () => AudioContext;
  /**
   * Override for audio fetches (the player calls this to load each track's
   * URL). Manifest and peaks are fetched via the global `fetch` (which tests
   * stub through `vi.stubGlobal`). Defaults to `globalThis.fetch`.
   */
  fetchImpl?: typeof fetch;
  /** Client-side redirect; injectable for tests. Defaults to location.assign. */
  navigate?: (path: string) => void;
}

export interface BootSuccess {
  unmount: () => void;
  /**
   * SP3: switch the player to another share in place (teardown→fetch→mount).
   * Only present on the player route — the full-bleed views (login / library /
   * dashboard / setlist) return `{ unmount }` without it.
   */
  loadSong?: (shareId: string) => Promise<void>;
}

export interface BootError {
  error: { kind: string; message?: string };
}

export type BootResult = BootSuccess | BootError;

function byId(id: string): Element {
  const el = document.getElementById(id);
  if (!el) {
    throw new Error(`mount point #${id} not found`);
  }
  return el;
}

/**
 * Switch the shell into a full-bleed, non-player view (library / setlist /
 * login gate). The index.html shell ships with the player-grid scaffolding
 * (#share-topbar / #share-rail / #share-console placeholders) which would
 * otherwise reserve grid tracks and box the view into a single cell. We clear
 * those children and tag `<body data-view=…>` so `library.css` can collapse
 * the grid and hide the player chrome. (B-064)
 *
 * `data-ready` is set to 'true' immediately: the global boot-dim rule
 * (`#app:not([data-ready="true"]) { opacity: .7 }`, layout.css) exists to fade
 * in the player while its audio loads. Full-bleed views have no async player
 * load to gate — their shell renders synchronously right after this call — so
 * they must mark themselves ready at once. Leaving it 'false' (the old
 * behaviour) pinned /library, /setlist, /login, /dashboard and /admin at 70%
 * opacity permanently, washing out the whole page and dropping "past" setlist
 * rows below the WCAG-AA contrast floor.
 */
export function enterFullBleedView(view: 'library' | 'setlist' | 'login' | 'dashboard' | 'words', appHost: HTMLElement): void {
  appHost.innerHTML = '';
  appHost.setAttribute('data-ready', 'true');
  if (typeof document !== 'undefined' && document.body) {
    document.body.setAttribute('data-view', view);
  }
}

/**
 * Mount the member app-header above a full-bleed view's content. Clears #app,
 * appends a sticky header host + a scrollable content host, mounts the header,
 * then lets the caller render its content into the content host. Returns the
 * header handle so the route's unmount can tear it down.
 */
export function mountFullBleedHeader(
  appHost: HTMLElement,
  apiBase: string,
  navigate: (path: string) => void,
  renderContent: (content: HTMLElement) => void,
): AppHeaderHandle {
  appHost.innerHTML = '';
  const headerHost = document.createElement('header');
  headerHost.id = 'app-header-host';
  const content = document.createElement('div');
  content.className = 'fullbleed-content';
  appHost.appendChild(headerHost);
  appHost.appendChild(content);
  const header = mountAppHeader(headerHost, { apiBase, navigate, onReport: () => openFeedbackModal(), eraseOffline: eraseOfflineData });
  renderContent(content);
  return header;
}

/** Undo {@link enterFullBleedView} — used by the view's unmount(). */
export function exitFullBleedView(): void {
  if (typeof document !== 'undefined' && document.body) {
    document.body.removeAttribute('data-view');
  }
}

// The player-grid scaffolding that ships inside #app in index.html. The login
// gate (and every full-bleed view) wipes #app with innerHTML='', which destroys
// these mount points. When the gate hands back to the player (post-auth on a
// gated share link) the shell must be rebuilt first — otherwise mountShell's
// byId('share-rail') throws and the user is left on a blank page. (web review P1)
const PLAYER_SHELL_SCAFFOLD =
  '<header id="share-topbar"></header>' +
  '<aside id="share-rail"><div id="setlist-panel"></div></aside>' +
  '<main id="share-console"><div id="share-rehearsal-view" class="is-active">' +
  '<div id="share-player-panel"></div><aside id="share-takes-sidebar"></aside></div></main>' +
  '<footer id="share-promo-footer"></footer>' +
  '<div id="share-promo-modal-host"></div>' +
  '<div id="share-share-modal-host"></div>';

/** Rebuild the player-grid mount points inside #app after a full-bleed view
 *  (login gate) wiped them, so the player can mount. */
function restorePlayerShell(appHost: HTMLElement): void {
  appHost.innerHTML = PLAYER_SHELL_SCAFFOLD;
  appHost.setAttribute('data-ready', 'false');
  if (typeof document !== 'undefined' && document.body) {
    document.body.removeAttribute('data-view');
  }
}

function renderInvalidShareId(host: Element): void {
  host.textContent = '';
  const wrap = document.createElement('div');
  wrap.className = 'error-screen';
  wrap.dataset.errorKind = 'invalid-share-id';
  const h = document.createElement('h1');
  h.textContent = 'Invalid share link';
  const p = document.createElement('p');
  p.textContent = 'This link is malformed. Please check it and try again.';
  wrap.appendChild(h);
  wrap.appendChild(p);
  host.appendChild(wrap);
}

export function renderErrorScreen(
  host: Element,
  error: { kind: string; teamName?: string | null },
  onRetry?: () => void,
): void {
  host.textContent = '';
  const wrap = document.createElement('div');
  wrap.className = 'error-screen';
  wrap.dataset.errorKind = error.kind;

  const titleKey = error.kind === 'revoked' ? 'errorScreen.revokedTitle'
    : error.kind === 'not-a-member' ? 'errorScreen.notMemberTitle'
    : error.kind === 'not-found' ? 'errorScreen.notFoundTitle'
    : error.kind === 'offline' ? 'errorScreen.offlineTitle'
    : 'errorScreen.loadFailedTitle';
  const bodyKey = error.kind === 'revoked' ? 'errorScreen.revokedBody'
    : error.kind === 'not-a-member' ? (error.teamName ? 'errorScreen.notMemberBodyTeam' : 'errorScreen.notMemberBody')
    : error.kind === 'not-found' ? 'errorScreen.notFoundBody'
    : error.kind === 'offline' ? 'errorScreen.offlineBody'
    : 'errorScreen.loadFailedBody';

  const h = document.createElement('h1');
  h.textContent = t(titleKey);
  const p = document.createElement('p');
  p.textContent = t(bodyKey, error.teamName ? { team: error.teamName } : undefined);
  wrap.appendChild(h);
  wrap.appendChild(p);

  // Повтор не лечит ни закрытый доступ, ни чужую команду — только сеть.
  if (onRetry && error.kind !== 'revoked' && error.kind !== 'not-found' && error.kind !== 'not-a-member') {
    const btn = document.createElement('button');
    btn.className = 'error-retry';
    btn.textContent = t('errorScreen.retryBtn');
    btn.addEventListener('click', () => onRetry());
    wrap.appendChild(btn);
  }
  host.appendChild(wrap);
}

function isNotFoundError(message: string): boolean {
  return /404|not.found/i.test(message);
}

/** Transient error kinds worth retrying automatically (reload on 'online') or via the retry button. */
export function shouldAutoRetry(kind: string): boolean {
  return kind === 'load-failed' || kind === 'offline';
}

/**
 * Task 12 (admin revamp): the SPA no longer mounts the admin shell inline —
 * `/admin` is now a standalone Tabler page (served as `admin.html` by the
 * Cloudflare `_redirects` + Vite MPA build; `/ops` 302s to `/admin` at the
 * edge). This is the SPA-side backstop for environments where that edge
 * routing doesn't apply (e.g. a stale service-worker cache, or `vite dev`
 * without the `_redirects` file) — if `boot()` ever sees one of these paths,
 * it should bounce out to the real page rather than render anything itself.
 *
 * Returns the path to redirect to, or `null` if `pathname` isn't a legacy
 * admin/ops path.
 */
export function resolveLegacyAdminPath(pathname: string): string | null {
  if (pathname === '/admin' || pathname.startsWith('/admin/')) return '/admin';
  if (pathname === '/ops') return '/admin';
  return null;
}

/** Thrown when the login gate has already been mounted; callers must not render the error screen. */
class GateShownError extends Error {
  constructor() { super('gate-shown'); this.name = 'GateShownError'; }
}

export async function boot(opts: BootOpts = {}): Promise<BootResult> {
  // Тема — до монтирования любого маршрута: иначе экран на мгновение
  // показывает не тот набор цветов, а холодный заход в плеер не показывает
  // выбранный вовсе (B-260915).
  applyBootTheme();

  // Fire-and-forget Service Worker registration. On first install, in-flight
  // fetches bypass the SW entirely (browser semantics) — caching kicks in
  // from the second navigation onward, after `clients.claim()`. We don't
  // block boot on activation; that would add latency for no benefit. Errors
  // are swallowed inside registerServiceWorker — SW is progressive
  // enhancement, never required.
  void registerServiceWorker();

  // Просим браузер не вытеснять наше хранилище. Умолчание — «по возможности»:
  // Safari чистит данные сайта, к которым не обращались около семи дней, то
  // есть ровно интервал между двумя воскресеньями. Ответ пока никуда не идёт —
  // его потребитель появится вместе с кэшем слов; здесь важно, что просьба
  // прозвучала на каждом запуске.
  void requestPersistentStorage();

  // PostHog (web #1): analytics + the $feedback channel the report modal uses.
  // Fire-and-forget; no-ops (and never loads posthog-js) without the key.
  void initPosthog();

  const cdnBase = opts.cdnBase ?? DEFAULT_CDN_BASE;
  const fetchImpl =
    opts.fetchImpl ?? globalThis.fetch.bind(globalThis);

  const appHost = document.getElementById('app');
  if (!appHost) {
    throw new Error('boot: #app element not found');
  }

  // Auto-retry: if a transient error screen is showing when connectivity
  // returns, reload rather than making the user find the retry button.
  if (typeof window !== 'undefined') {
    window.addEventListener('online', () => {
      const es = appHost.querySelector('.error-screen') as HTMLElement | null;
      if (es && shouldAutoRetry(es.dataset.errorKind ?? '')) location.reload();
    });
  }

  const navigate = opts.navigate ?? ((path: string) => { if (typeof location !== 'undefined' && typeof location.assign === 'function') location.assign(path); });

  // Legacy in-SPA /admin and /ops routes (see resolveLegacyAdminPath) — bounce
  // out to the standalone /admin page BEFORE any normal SPA routing runs.
  const legacyAdminPath = resolveLegacyAdminPath(location.pathname);
  if (legacyAdminPath) {
    if (typeof location !== 'undefined' && typeof location.replace === 'function') location.replace(legacyAdminPath);
    return { unmount: () => {} };
  }

  // Standalone login (/login): external landing's "Sign in" links here. Also
  // the guest setlist screen's "Sign in" (задача D2, setlist-screen/route.ts,
  // buildSignInHandler) — `?next=` there is the SAME guest URL (path + `?g=`),
  // so a signed-in cookie on reload reads it as a member, no extra redirect.
  if (isLoginPath(location.pathname)) {
    enterFullBleedView('login', appHost);
    const API_BASE = '';
    // Same-origin-only guard: `next` is attacker-visible (a query param on a
    // link anyone can share) — only a path starting with a single `/` is
    // accepted, `//host/...` (protocol-relative) is rejected same as any
    // other absolute URL.
    const nextParam = new URLSearchParams(location.search).get('next');
    const next = nextParam && nextParam.startsWith('/') && !nextParam.startsWith('//') ? nextParam : null;
    const me = await fetchMe(API_BASE);
    if (me.ok) { navigate(next ?? roleHome(me.me)); return { unmount: () => { appHost.innerHTML = ''; exitFullBleedView(); } }; }
    mountLoginGate(appHost, {
      teamName: null,
      apiBase: API_BASE,
      context: 'portal',
      // Route through the roleHome seam (not a literal path) — keeps this in
      // sync with roleHome's post-login destination. Just authenticated →
      // fetchMe succeeds. `next` (when present and same-origin) wins over it.
      onAuthed: async () => { const after = await fetchMe(API_BASE); navigate(next ?? (after.ok ? roleHome(after.me) : '/library')); },
    });
    return { unmount: () => { appHost.innerHTML = ''; exitFullBleedView(); } };
  }

  // Invite accept (/invite/<token>): context screen ("you're invited to <team>")
  // before any login friction; the OTP gate mounts on this SAME route on Accept
  // when signed out, so the token never has to survive a redirect.
  const inviteToken = parseInviteToken(location.pathname);
  if (inviteToken) {
    enterFullBleedView('library', appHost);
    const API_BASE = '';
    await mountInviteView(appHost, {
      token: inviteToken,
      preview: (tok) => previewInvite(tok, API_BASE),
      accept: async (tok) => {
        const r = await acceptInvite(tok, API_BASE);
        return r.ok ? { ok: true, teamId: r.teamId } : { ok: false, error: r.error };
      },
      me: async () => { const m = await fetchMe(API_BASE); return (m.ok && m.me.email) ? { email: m.me.email } : null; },
      navigate: (path) => { if (typeof location !== 'undefined') location.assign(path); },
      mountLogin: (h, opts) => mountLoginGate(h, { teamName: opts.teamName, apiBase: API_BASE, onAuthed: opts.onAuthed, context: 'invite' }),
      // Escape hatch for email_mismatch: sign out the wrong account so doAccept can
      // re-drive on this SAME /invite/<token> route instead of navigating to /login
      // (which would bounce an authed visitor straight to roleHome — a dead end).
      // Report success/failure (auth.ts's signOut already returns {ok} — previously
      // discarded here) so invite-view can surface a failure message instead of
      // silently re-driving doAccept against the still-wrong-account session.
      signOut: async () => { const r = await signOut(API_BASE); return r.ok; },
    });
    return { unmount: () => { appHost.innerHTML = ''; exitFullBleedView(); } };
  }

  // Root (/): authed → role home; else → login.
  if (location.pathname === '/' || location.pathname === '') {
    const API_BASE = '';
    const me = await fetchMe(API_BASE);
    navigate(me.ok ? roleHome(me.me) : '/login');
    return { unmount: () => {} };
  }

  // Team-library view (/library): cookie-authed aggregate, OTP gate on 401.
  if (isLibraryPath(location.pathname)) {
    // These views replace the rehearsal player. Clear the shell slots
    // (#share-topbar/#share-rail/#share-console placeholders from index.html)
    // so library/login mount full-bleed, and flag the body so library.css can
    // collapse the player grid and hide the player chrome (B-064).
    enterFullBleedView('library', appHost);
    const API_BASE = '';
    const goToLibraryPath = (path: string): void => { if (typeof location !== 'undefined') location.assign(path); };
    let headerHandle: AppHeaderHandle | null = null;
    let libraryContent: HTMLElement | null = null;
    // Волна G. Прежде здесь лежал `lastTeams` — последний удачный список,
    // живущий только до перезагрузки вкладки, с пометкой «persisted — это
    // будущая волна офлайна». Волна пришла: список переехал в IndexedDB и
    // теперь переживает закрытие приложения, перезагрузку и отсутствие сети.
    //
    // Личность нужна ДО ответа /api/me: без неё нечем построить ключ, а
    // значит нечего показать в первую секунду — ровно ту, ради которой волна
    // делается. Поэтому id последнего вошедшего лежит рядом, в localStorage.
    // Это непрозрачный идентификатор в собственном браузере человека, не
    // секрет; ошибка здесь ничего не открывает, потому что сам кэш всё равно
    // читается только по совпадению ключа.
    let offlineUserId = readOfflineUserId();
    // idbBackend отдаёт null там, где IndexedDB нет (приватный режим, старый
    // WebView). Память в этом случае — не «тоже кэш», а способ не городить
    // ветку `if (store)` в каждой точке: она живёт до перезагрузки и молча
    // исчезает, что честнее наполовину отключённой функции.
    const kv = idbBackend() ?? memoryBackend();
    let offlineStore = offlineUserId ? createOfflineStore(kv, offlineUserId) : null;
    let cachedTeams: import('./library.js').TeamLibrary[] | null = null;
    let lastSuccessAt: string | null = null;
    let syncing = false;
    // ⚠️ КОНТРАКТ ПОЛЯ `online` (шапка offline/freshness.ts) — уточнено
    // дефектом 4Б живой приёмки волны G. Прежний запрет («navigator.onLine
    // сюда нельзя вовсе») был сформулирован ШИРЕ факта: `navigator.onLine`
    // врёт ТОЛЬКО в сторону `true` (зальный Wi-Fi с мёртвым аплинком,
    // captive-портал — ровно сценарий волны); `false` достоверен всегда.
    // Правило теперь: `online` = исход fetchLibrary, ВЕТИРОВАННЫЙ этой
    // достоверной половиной. Успешный ответ пять минут назад плюс браузер,
    // только что потерявший сеть, обязаны показать «офлайн» — заменить
    // `lastFetchOk` голым `navigator.onLine` НЕЛЬЗЯ: тогда живой туннель в
    // метро с ответившим один раз сервером десять минут назад читался бы
    // «свежо» без единого нового запроса.
    //
    // Начальное `false` НЕ наблюдаемо: до первого ответа `syncing` истинно, а
    // «обновляю» в freshness() побеждает всё остальное.
    let lastFetchOk = false;
    const isBrowserOffline = (): boolean =>
      typeof navigator !== 'undefined' && navigator.onLine === false;
    const freshnessNow = (): import('./offline/freshness.js').FreshnessState =>
      freshness({ online: lastFetchOk && !isBrowserOffline(), syncing, lastSuccessAt, nowMs: Date.now() });
    // Докачка отменяется при уходе с библиотеки: см. третье свойство в
    // шапке offline/prefetch.ts. Контроллер один на всё время жизни ветки —
    // отдельный на каждый прогон плодил бы незакрытые докачки при каждой
    // перерисовке (их тут шесть: retry, импорт слов, снятие, метка главной…).
    const prefetchAbort = new AbortController();
    // Owner's current per-version visibility, fetched once per library mount and
    // memoised — the Viewers panel reads it to pre-select the active state.
    // Owner-only endpoint; non-owners simply get an empty list.
    let mySharesPromise: Promise<import('./shares-api.js').OwnedShare[]> | null = null;
    // Дефект 4Б: снимок опций последней отрисовки — читает `onOffline` ниже,
    // которому взять их больше неоткуда (полных meResult/dashResult у него
    // нет, а гонять renderLibrary() ради перерисовки строки — тратить сеть,
    // которой только что не стало, см. дефект 4А).
    let lastViewOpts: ReturnType<typeof buildViewOpts> | null = null;
    const loadVisibility = async (shareId: string): Promise<'public' | 'team' | null> => {
      if (!mySharesPromise) {
        mySharesPromise = listMyShares(API_BASE).then((r) => (r.ok ? r.shares : []));
      }
      const shares = await mySharesPromise;
      return shares.find((s) => s.id === shareId)?.visibility ?? null;
    };

    // Mounts the persistent header once (and again after a post-gate
    // re-auth, which wipes #app) with a loading skeleton in its content slot.
    // /api/library travels over a phone's mobile connection in a venue and
    // can take a couple of seconds — without this the first paint used to be
    // a blank #app until the fetch resolved.
    const mountSkeleton = (): void => {
      headerHandle = mountFullBleedHeader(appHost, API_BASE, goToLibraryPath, (content) => {
        libraryContent = content;
        mountLibraryView(content, { teams: [], navigate: goToLibraryPath, loading: true });
      });
    };

    // Набор опций вьюхи собирается функцией, а не литералом на месте:
    // рисуем мы ДВАЖДЫ — сначала из кэша, до всякой сети, потом ответом
    // сервера, — и две копии литерала разъехались бы на первой же правке.
    // `meEmail`/`quota` приезжают только со вторым рисованием: до ответа
    // /api/me неизвестно, кто смотрит, а притвориться владельцем на первом
    // кадре хуже, чем показать список без владельческих органов.
    const buildViewOpts = (
      meEmail: string | null,
      quota: { used: number; limit: number } | null,
    ) => ({
      navigate: goToLibraryPath,
      meEmail,
      quota,
      loadViewers: (id: string) => listViewers(id, API_BASE),
      loadVisibility,
      revokeViewer: (id: string, uid: string) => apiRevokeViewer(id, uid, API_BASE),
      // Успешная смена видимости РОНЯЕТ мемоизированный ответ
      // `/api/my-shares`: следующее открытие листа обязано прочитать
      // видимость заново. Волна E с мемоизацией жила — панель читателей
      // строилась один раз на монтирование; лист управления песней сделал
      // повторное открытие дешёвым, и устаревший ответ стал виден глазом:
      // владелец переключает Team→Public, закрывает лист, открывает снова
      // убедиться — и читает «Team». Это ровно то «врать про состояние»,
      // против которого написаны правила раздела записей.
      //
      // Сброс, а не точечная правка кэша: ответ сервера и есть источник
      // правды, а второй экземпляр той же правды (подмена поля в
      // запомненном массиве) разошёлся бы с ним молча — ровно так же, как
      // разошлась мемоизация.
      setVisibility: (id: string, vis: 'public' | 'team', tid: string | null) =>
        apiSetVisibility(id, vis, tid, API_BASE).then((r) => {
          if (r.ok) mySharesPromise = null;
          return r;
        }),
      // Перенос слов из живых текстовых ссылок в записи песен (волна D).
      // Виден только владельцу — решает сама вьюха по `quota`, здесь только
      // проводка. `onWordsImported` перезапрашивает библиотеку: перенесённые
      // слова обязаны появиться на строках сразу, без перезагрузки страницы.
      backfillWords: () => backfillSongTexts(API_BASE),
      onWordsImported: () => { void renderLibrary(); },
      // Волна F, задача 7: раздел «Слова» листа управления песней. Обе
      // записи — действия владельца (маршрут отвечает 403 not_owner
      // товарищу по команде), поэтому вьюха показывает раздел только у
      // СВОИХ слов, а сервер проверяет то же самое заново.
      setWordsVisibility: (id: string, visibility: 'team' | 'public') =>
        setSongTextVisibility(id, visibility, API_BASE),
      removeWords: (id: string) => removeSongText(id, API_BASE),
      // Снятые слова обязаны уйти со строки сразу, без перезагрузки
      // страницы — тем же приёмом, что onWordsImported выше.
      onWordsRemoved: () => { void renderLibrary(); },
      // Снятие записи с публикации. Тот же адрес, которым пользуется
      // десктоп, но доказательство права — сессионная cookie: `SHARE_TOKEN`
      // одинаков во всех установках, и браузеру его не отдают.
      removeVersion: (id: string) => removeShare(id, API_BASE),
      // Снятая запись обязана уйти из списка сразу, без перезагрузки
      // страницы — тем же приёмом, что onWordsRemoved выше.
      onVersionRemoved: () => { void renderLibrary(); },
    });

    const renderLibrary = async (): Promise<void> => {
      // Запрос уходит ПЕРВЫМ и не ждёт чтения кэша: IndexedDB отвечает
      // быстро, но занимать этим сетевую задержку незачем.
      syncing = true;
      const pending = Promise.all([fetchLibrary(API_BASE), fetchMe(API_BASE), fetchDashboard(API_BASE)]);

      // ── Кэш первым. Сети не ждём никогда: список рисуется из того, что
      // уже лежит на телефоне, и только потом уточняется ответом сервера.
      // Один раз за жизнь ветки — повторные заходы (retry, перерисовки после
      // правок) уже держат список в `cachedTeams`.
      if (offlineStore && cachedTeams === null) {
        const cached = await offlineStore.loadLibrary();
        if (cached && libraryContent) {
          cachedTeams = cached.teams;
          lastSuccessAt = cached.savedAt;
          mountLibraryView(libraryContent, {
            ...buildViewOpts(null, null), teams: cached.teams, freshness: freshnessNow(),
          });
        }
      }

      const [result, meResult, dashResult] = await pending;
      syncing = false;
      // Единственный источник `online` — см. объявление `lastFetchOk` выше.
      lastFetchOk = result.ok;

      // Личность могла приехать впервые (первый вход на этом телефоне) или
      // смениться (вошёл другой человек). И то, и другое означает ДРУГОЕ
      // хранилище: ключи у них не пересекаются по построению.
      if (meResult.ok && meResult.me.userId !== offlineUserId) {
        offlineUserId = meResult.me.userId;
        try { localStorage.setItem(OFFLINE_UID_KEY, offlineUserId); } catch { /* приватный режим */ }
        offlineStore = createOfflineStore(kv, offlineUserId);
        cachedTeams = null;
        lastSuccessAt = null;
      }
      // Stitch this browser to the desktop person (telemetry audit 2026-07-28).
      // /library is cookie-authed — a 401 lands in the needsAuth branch below and
      // fetchMe returns {ok:false}, so `meResult.ok` IS the "OTP login already
      // told us who this is" signal. Same users.id the desktop identifies with,
      // and ONLY the id: no email, no name (see identifyOwner's privacy note).
      // The public-share player route deliberately does not do this — anonymous
      // viewers stay anonymous.
      if (meResult.ok) identifyOwner(meResult.me.userId);
      // Дефект 2 живой приёмки волны G: кэш вошедшего для офлайн-показа шапки
      // (ui/app-header.ts читает его через offline/open-store.js#loadCachedMe
      // по умолчанию — здесь только запись). Рядом с записью `bw.lib.uid`
      // выше: тот же момент, тот же успешный /api/me. Без await и с
      // молчаливым глотанием отказа — офлайн усиление, не несущая часть.
      if (meResult.ok && offlineStore) {
        void offlineStore.saveMe({ email: meResult.me.email, teams: meResult.me.teams }).catch(() => {});
      }
      // Owner-only storage chip: surface team usage in the library header when the
      // signed-in user owns the team. Members / no-team → null (chip hidden).
      const ownerQuota = (dashResult.ok && dashResult.role === 'owner' && dashResult.quota)
        ? { used: dashResult.quota.used, limit: dashResult.quota.limit }
        : null;
      const viewOpts = buildViewOpts(meResult.ok ? meResult.me.email : null, ownerQuota);
      lastViewOpts = viewOpts;

      // Судьбу кэша решает `syncAction`, а исполняет `applySyncAction`. В
      // main.ts не остаётся НИ ОДНОГО правила: ни «отказ — стереть», ни
      // «401 — стереть». Оба замка волны стоят в тех модулях, и оба
      // поведенческие.
      const action = syncAction(result, new Date().toISOString());

      if (action.kind === 'save-and-reconcile') {
        cachedTeams = action.teams;
        lastSuccessAt = action.at;
        mountLibraryView(libraryContent!, { ...viewOpts, teams: action.teams, freshness: freshnessNow() });
        const store = offlineStore;
        const uid = offlineUserId;
        if (store && uid) {
          // ПОСЛЕ отрисовки и без await: список уже на экране, а запись
          // снимка и обход ключей в IndexedDB не имеют права задержать его
          // ни на миллисекунду. Отказ хранилища глотается сознательно —
          // офлайн это усиление, а не несущая часть.
          void (async (): Promise<void> => {
            await applySyncAction({ store, userId: uid, action }).catch(() => {});
            // Задача D4 — состав предстоящих сетлистов и слова именно их
            // песен, ПЕРЕД общей докачкой слов ниже: то же «сначала
            // прибрались», плюс приоритет ближайшему служению.
            await runSetlistPrefetch({
              store, library: action.teams, apiBase: API_BASE, signal: prefetchAbort.signal,
            }).catch(() => {});
            // Докачка — ПОСЛЕ сверки, а не параллельно с ней: пересечения по
            // ключам нет (сверка сносит отсутствующее в ответе, докачка
            // пишет присутствующее), но порядок «сначала прибрались, потом
            // наполняем» дешевле для квоты хранилища на телефоне.
            await prefetchWords({
              store,
              teams: action.teams,
              fetchOne: async (id) => {
                const r = await fetchSongText(id, API_BASE);
                return r.ok ? { ok: true, payload: r.payload } : { ok: false };
              },
              signal: prefetchAbort.signal,
            }).catch(() => {});
          })();
        }
        return;
      }

      if (!result.ok && result.needsAuth) {
        // Сессии нет — показываем вход, как и до волны G. Кэш при этом
        // ОСТАЁТСЯ: `syncAction` отдал `keep`, и ниже его никто не трогает.
        // Решение владельца продукта — «приоритет, чтобы кеш всегда был»:
        // один 401 от прокси посреди служения не имеет права обнулить
        // библиотеку. Стирание осталось только явным (выход, кнопка).
        mountLoginGate(appHost, {
          teamName: null,
          apiBase: API_BASE,
          context: 'portal',
          onAuthed: async () => { appHost.innerHTML = ''; mountSkeleton(); await renderLibrary(); },
        });
        return;
      }

      // `keep` — ответ не пришёл. Кэш НЕ трогаем ничем: ни сверкой, ни
      // стиранием. На экране остаётся то, что уже есть, — и теперь это не
      // «список этой вкладки», а список с диска, переживший перезагрузку.
      mountLibraryView(libraryContent!, {
        ...viewOpts,
        teams: cachedTeams ?? [],
        fetchFailed: true,
        freshness: freshnessNow(),
        onRetry: () => { void renderLibrary(); },
      });
    };

    // Сеть вернулась — обновляемся сами, без участия человека. Событие здесь
    // ПОДСКАЗКА «стоит попробовать снова», а не источник состояния: врёт оно
    // ровно так же в положительную сторону, как navigator.onLine (см.
    // freshnessNow выше), но лишний запрос безвреден.
    const onBackOnline = (): void => { void renderLibrary(); };
    // Дефект 4Б живой приёмки волны G, владелец продукта дословно: «когда
    // отпадает интернет, приложение... должно сразу в офлайн-мод переходить
    // и не переставать работать без перезагрузки». До этой правки на `offline`
    // никто не подписывался: строка свежести узнавала о потере сети только на
    // СЛЕДУЮЩЕЙ попытке `/api/library` — то есть после возврата `online` или
    // перезапуска приложения, ровно то, на что пожаловались. Полный
    // renderLibrary() здесь НЕ зовём — сети всё равно нет, а зовущийся запрос
    // без таймера и есть замирание, которое чинит дефект 4А в sw.js;
    // перерисовываем тем, что уже на экране (`cachedTeams`), с уже верным
    // (браузер сказал `false` — достоверно) состоянием строки.
    const onOffline = (): void => {
      if (!libraryContent) return;
      mountLibraryView(libraryContent, {
        ...(lastViewOpts ?? buildViewOpts(null, null)),
        teams: cachedTeams ?? [],
        fetchFailed: true,
        freshness: freshnessNow(),
        onRetry: () => { void renderLibrary(); },
      });
    };
    if (typeof window !== 'undefined') {
      window.addEventListener('online', onBackOnline);
      window.addEventListener('offline', onOffline);
    }

    mountSkeleton();
    await renderLibrary();
    return { unmount: () => {
      prefetchAbort.abort();
      if (typeof window !== 'undefined') {
        window.removeEventListener('online', onBackOnline);
        window.removeEventListener('offline', onOffline);
      }
      headerHandle?.unmount(); appHost.innerHTML = ''; exitFullBleedView();
    } };
  }

  // Team dashboard (/dashboard): cookie-authed read-only team overview, OTP gate on 401.
  if (isDashboardPath(location.pathname)) {
    enterFullBleedView('dashboard', appHost);
    const API_BASE = '';
    let headerHandle: AppHeaderHandle | null = null;
    let lastJoinError: string | null = null;
    let lastInviteMsg: string | null = null;

    const renderDashboard = async (): Promise<void> => {
      const result = await fetchDashboard(API_BASE);
      if (result.ok) {
        const joinError = lastJoinError;
        const inviteMsg = lastInviteMsg;
        lastInviteMsg = null;
        headerHandle = mountFullBleedHeader(
          appHost,
          API_BASE,
          (path) => { if (typeof location !== 'undefined') location.assign(path); },
          (content) => {
            mountDashboardView(content, {
              data: {
                team: result.team,
                role: result.role,
                joinCode: result.joinCode,
                members: result.members,
                selfUserId: result.selfUserId,
                quota: result.quota,
                joinError,
                inviteMsg,
              },
              navigate: (path) => { if (typeof location !== 'undefined') location.assign(path); },
              onJoin: async (code) => {
                lastJoinError = null;
                const r = await joinTeam(code, API_BASE);
                if (r.ok) {
                  appHost.innerHTML = '';
                  await renderDashboard();
                } else {
                  lastJoinError = r.error;
                  appHost.innerHTML = '';
                  await renderDashboard();
                }
              },
              onLeave: async () => {
                if (!result.team || !result.selfUserId) return;
                const r = await leaveTeam(result.team.id, result.selfUserId, API_BASE);
                if (r.ok) {
                  appHost.innerHTML = '';
                  await renderDashboard();
                }
              },
              onRotateCode: async () => {
                if (!result.team) return;
                const r = await rotateCode(result.team.id, API_BASE);
                if (r.ok) { appHost.innerHTML = ''; await renderDashboard(); }
              },
              onInvite: async (email) => {
                if (!result.team) return;
                const r = await invite(result.team.id, email, getLocale(), API_BASE);
                lastInviteMsg = inviteMsgFor(r);
                appHost.innerHTML = '';
                await renderDashboard();
              },
              onRemove: async (userId) => {
                if (!result.team) return;
                const r = await leaveTeam(result.team.id, userId, API_BASE);
                if (r.ok) { appHost.innerHTML = ''; await renderDashboard(); }
              },
            });
          },
        );
        return;
      }
      if (result.needsAuth) {
        mountLoginGate(appHost, {
          teamName: null,
          apiBase: API_BASE,
          context: 'portal',
          onAuthed: async () => { appHost.innerHTML = ''; await renderDashboard(); },
        });
        return;
      }
      appHost.innerHTML = `<p class="library-error">${t('dashboard.loadError')}</p>`;
    };

    // Accept a one-time invite token from ?invite= before the first render.
    const inviteToken = typeof location !== 'undefined' ? new URLSearchParams(location.search).get('invite') : null;
    if (inviteToken) {
      await acceptInvite(inviteToken, API_BASE);
      if (typeof history !== 'undefined') history.replaceState(null, '', location.pathname);
    }

    await renderDashboard();
    return { unmount: () => { headerHandle?.unmount(); appHost.innerHTML = ''; exitFullBleedView(); } };
  }

  // Экран сетлиста (единый сетлист, фазы 1-4): /setlist/<id>, /setlist/w/<uuid>
  // и /s/<uuid>[?g=] — вся ветка вынесена в setlist-screen/boot.ts (дисциплина
  // роста: монолит несёт только вызов). `null` — путь не подошёл ни под один
  // из трёх, boot() продолжает свою обычную цепочку маршрутов ниже.
  const setlistScreen = await bootUnifiedSetlistRoute({ appHost, fetchImpl });
  if (setlistScreen) return setlistScreen;

  // ── Текстовая ссылка на сетлист (/t/<uuid>) ───────────────────────────────
  // Слова и аккорды, без аудио и без логина: «кину в чат слова к воскресенью».
  //
  // Стоит ПЕРЕД разбором аудио-шейра, но порядок здесь — не защита: `/t/<uuid>`
  // двухсегментный, и `parseShareIdFromPath` его не берёт вовсе (тест на это
  // есть в test/router.test.ts). Защита в другом — в префиксе: голый UUID под
  // правило shareId подходит целиком, поэтому у текстовой ссылки свой сегмент.
  const textShareId = parseTextShareId(location.pathname);
  if (textShareId) {
    const API_BASE = '';
    // Задача D1 — та же старая-ссылка проверка, что у /setlist/w/<uuid> выше
    // (setlist-screen/boot.ts): до РИСОВАНИЯ страницы слов проверяем, не
    // завела ли ссылка на унифицированный /s/<id> (участник по библиотеке или
    // гостевой ключ). ДО enterFullBleedView('words', …) — иначе страница слов
    // мелькнула бы на экране на время сетевого похода прежде, чем
    // location.replace её снимет. С кнопки Live (`?live=1`) проверки нет:
    // читатель уже пришёл с экрана сетлиста, и редирект вернул бы его туда же
    // по кругу (B-261002).
    const legacyPath = isLiveWordsEntry(location.search)
      ? null
      : await legacyLinkRedirectPath(textShareId, API_BASE, fetchImpl);
    if (legacyPath) {
      if (typeof location !== 'undefined') location.replace(legacyPath);
      return { unmount: () => {} };
    }
    enterFullBleedView('words', appHost);
    let view: WordsViewHandle | null = null;
    // Клиента лайва заводит ТОТ, кто знает адрес ссылки, — он же его и гасит.
    // Страница им пользуется и не владеет: иначе повторная попытка загрузки
    // оставляла бы за собой второй цикл опроса, а частота опроса читателей —
    // единственный настоящий рычаг цены лайва.
    let live: LiveClient | null = null;
    // Поколение чтения — та же защита, что у loadSong ниже. Кнопка повтора не
    // гасится и не дебаунсится, поэтому два щелчка дают два запроса в полёте, а
    // вернуться они могут в обратном порядке. Пока ответы одинаковы, это
    // незаметно; но если между щелчками ссылку ЗАВЕРШИЛИ, отставший ответ
    // показал бы залу слова, чтение которых только что прекратили, — а
    // «завершить» обязано заканчивать чтение (та же причина, по которой
    // нагрузка отдаётся с no-store и сметается из R2).
    let wordsGen = 0;

    const renderWords = async (): Promise<void> => {
      const myGen = ++wordsGen;
      const res = await fetchTextShare(textShareId, API_BASE, fetchImpl);
      // Выход ДО снятия экрана: отставший ответ не трогает ни вид, ни лайв,
      // которые поставил более свежий запрос.
      if (myGen !== wordsGen) return;
      // Прежний экран снимается ПЕРЕД новым, и слой чтения вместе с ним:
      // повторная попытка после сбоя иначе оставила бы за собой слот в body.
      view?.unmount();
      view = null;
      appHost.innerHTML = '';
      if (res.state === 'ok') {
        // Расписание и длительности берутся из ТОЙ ЖЕ нагрузки, что читает
        // страница: якорь несёт только `songIdx`, а что это за песня и когда
        // она кончается — знание читателя, не сервера.
        let songs = wordsSongs(res.payload);
        // Ревизия содержимого, которую страница ДЕРЖИТ сейчас (спека §4.4).
        // `undefined` у воркера до выката — тогда сверять нечего и всё идёт как
        // раньше.
        // Стык «ревизия сказала, что устарело → привести страницу» живёт
        // отдельным модулем, а не здесь. Первая редакция была замыканием, и
        // сквозной ревью 17.09 показал цену: на этой границе оказались три
        // дефекта сразу, и все три были зелёными, потому что граница не
        // покрывалась ни одним тестом (спека §6 называет её замком фичи).
        const content = createContentSync({
          initialRev: res.contentRev,
          fetchPayload: () => fetchTextShare(textShareId, API_BASE, fetchImpl),
          isCurrent: () => myGen === wordsGen,
          apply: (p) => { songs = wordsSongs(p); view?.setPayload(p); },
        });
        live?.stop();
        live = createLiveClient({
          textShareId,
          apiBase: API_BASE,
          fetchImpl,
          // Расписание и длительности читаются ЧЕРЕЗ замыкание, а не снимком:
          // после перечитывания песни другие, и клиент, державший старую пару
          // функций, вёл бы лист по вчерашним секциям.
          sectionsOf: (i) => wordsSectionsOf(songs)(i),
          durationSecOf: (i) => wordsDurationSecOf(songs)(i),
          syncContent: (rev) => content.sync(rev),
        });
        view = mountWordsView(appHost, { payload: res.payload, live });
        return;
      }
      // Три «нет» — три РАЗНЫЕ страницы. 410 против 404 — решение Сергея от
      // 28.08: музыкант в зале должен понять, что ссылка была правильная и
      // истекла, а не что он ошибся ссылкой (причина — в шапке words-status.ts).
      if (res.state === 'ended') {
        renderTextShareEnded(appHost, { onLanding: () => navigate(LANDING_URL) });
        return;
      }
      if (res.state === 'not-found') {
        renderTextShareNotFound(appHost, { onLanding: () => navigate(LANDING_URL) });
        return;
      }
      renderTextShareError(appHost, { onRetry: () => { void renderWords(); } });
    };

    await renderWords();
    return {
      unmount: () => {
        view?.unmount();
        view = null;
        // Опрос переживает страницу, если его не снять: `stop()` снимает и
        // назначенный срок, и ответ, который ещё летит.
        live?.stop();
        live = null;
        appHost.innerHTML = '';
        exitFullBleedView();
      },
    };
  }

  // ── Страница слов ОДНОЙ песни (волна C): /words/<id> ──────────────────────
  // Вся логика — в song-words-view.ts (мандат роста: main.ts несёт только
  // проводку маршрута). Слой чтения там ЖЕ, что у сетлиста, но без списка под
  // ним — причина и устройство соседей ‹› расписаны в шапке того модуля.
  const songWordsId = parseSongWordsId(location.pathname);
  if (songWordsId) {
    enterFullBleedView('words', appHost);
    const API_BASE = '';
    // Гостевой пропуск (единый сетлист, фазы 3+4, задача D2) — слот «Слова»
    // гостевого экрана сетлиста (`/s/<uuid>?g=`) ведёт сюда с обоими query-
    // параметрами; участник (кука) их не кладёт вовсе, и без обоих сразу
    // маршрут ведёт себя байт-в-байт как раньше (guestWordsAccess, song-text-api.ts).
    const wordsSearch = new URLSearchParams(location.search);
    const wordsSetlistParam = wordsSearch.get('setlist');
    const wordsGuestKey = wordsSearch.get('g');
    const wordsGuestAccess = wordsSetlistParam && wordsGuestKey ? { setlistId: wordsSetlistParam, guestKey: wordsGuestKey } : undefined;
    const view: SongWordsViewHandle = mountSongWordsView(appHost, {
      id: songWordsId,
      apiBase: API_BASE,
      fetchImpl,
      navigate,
      // Волна G. Ни одного правила кэша здесь нет: «сеть или телефон», какая
      // это команда и что делать со снятой с публикации песней — целиком
      // offline/words-resolve.ts, ровно как у библиотеки решение живёт в
      // library-sync.ts + apply-sync.ts. Тут только проводка.
      resolveWords: (id) => resolveWordsForPage({
        store: openOfflineStore(),
        songTextId: id,
        fetchOne: () => fetchSongText(id, API_BASE, fetchImpl, wordsGuestAccess),
      }),
    });
    return {
      unmount: () => {
        view.unmount();
        appHost.innerHTML = '';
        exitFullBleedView();
      },
    };
  }

  const shareId = parseShareIdFromPath(location.pathname);
  if (!shareId) {
    renderInvalidShareId(appHost);
    const error = {
      kind: 'invalid-share-id',
      message: 'no shareId in path',
    };
    setState({ error });
    return { error };
  }

  // SP3: the player share becomes mutable so the setlist-rail can switch songs
  // in place. `activeShareId` starts at the URL's shareId; loadSong() advances it.
  let activeShareId = shareId;
  let currentAudioContext: AudioContext | null = null;
  // D3: rail/version-picker/words-only wiring lives in setlist-player-wiring.ts.
  let wiring: SetlistPlayerWiring | null = null;

  // The API base for gated manifest fetches. Same-origin in production
  // (worker intercepts /api/shares/*). Tests stub global.fetch so any URL
  // ending in manifest.json is served correctly regardless of base.
  const API_BASE = '';

  // Гостевой пропуск (единый сетлист, фазы 3+4, задача D2) — слот «Звук»
  // гостевого экрана сетлиста ведёт сюда с `?setlist=<id>&g=<key>`; участник
  // (кука) их не кладёт, и без обоих сразу манифест запрашивается как раньше.
  const manifestSearch = new URLSearchParams(location.search);
  const manifestSetlistParam = manifestSearch.get('setlist');
  const manifestGuestKey = manifestSearch.get('g');
  const manifestGuestAccess = manifestSetlistParam && manifestGuestKey
    ? { setlistId: manifestSetlistParam, guestKey: manifestGuestKey }
    : undefined;

  // ── Gated manifest fetch with gate-state handling ──────────────────────
  // Helper that fetches the manifest and returns { manifest, playbackToken }
  // or handles the gate states (needs-auth → login gate; revoked/error → error screen).
  // Returns null when boot should be aborted (gate displayed / error shown).
  type ManifestData = { manifest: ReturnType<typeof Object.create>; playbackToken: string | null };

  const fetchManifestGated = async (): Promise<ManifestData | null> => {
    const result = await loadManifest(activeShareId, API_BASE, manifestGuestAccess);
    if (result.state === 'ok') {
      return { manifest: result.manifest, playbackToken: result.playbackToken };
    }
    if (result.state === 'needs-auth') {
      // Show login gate. After auth, retry manifest fetch.
      mountLoginGate(appHost, {
        teamName: result.teamName,
        apiBase: API_BASE,
        onAuthed: async () => {
          // The gate wiped #app; rebuild the player scaffolding before mounting
          // (else mountShell's byId('share-rail') throws → blank page). login-gate
          // calls onAuthed fire-and-forget, so a throw here would be an unhandled
          // rejection — catch it and show the error screen instead. (web review P1)
          try {
            restorePlayerShell(appHost);
            const data = await fetchManifestGated();
            if (data) {
              await mountPlayer(data.manifest, data.playbackToken);
            }
          } catch (err) {
            const error = { kind: 'load-failed', message: err instanceof Error ? err.message : String(err) };
            renderFatalError(error, () => { if (typeof location !== 'undefined') location.reload(); });
          }
        },
      });
      return null;
    }
    // revoked / error can be reached both at first boot (no shell yet) AND during
    // an in-place song switch (shell mounted). renderFatalError tears the shell +
    // song down before wiping #app so a switch into a revoked/dead song doesn't
    // leak the rAF loop, backdrop, and subscriptions. (web review P2 — sibling of
    // the swapSong leak, found in cross-cutting review)
    if (result.state === 'revoked') {
      renderFatalError({ kind: 'revoked' });
      return null;
    }
    if (result.state === 'not-a-member') {
      renderFatalError({ kind: 'not-a-member', teamName: result.teamName });
      return null;
    }
    // state === 'error'
    const status = result.status;
    const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;
    const error = {
      kind: status === 404 ? 'not-found' : isOffline ? 'offline' : 'load-failed',
    };
    renderFatalError(error, () => { if (typeof location !== 'undefined') location.reload(); });
    return null;
  };

  // Helper that builds a gated fetch function for peaks/audio.
  // When a playback token is available, fetches go through the worker with
  // Authorization: Bearer pb:<token>. On 401, refreshes the token once.
  // When no token is available (legacy / test stub), falls back to direct CDN.
  const makeGatedFetch = (
    playbackToken: string | null,
    onTokenRefresh: (newToken: string | null) => void,
  ): typeof fetchImpl => {
    if (!playbackToken) {
      // No token → legacy CDN path (backward compat: tests/public bundles).
      return fetchImpl;
    }
    let currentToken = playbackToken;
    return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      const url = typeof input === 'string' ? input : input.toString();
      const res = await fetchImpl(url, {
        ...init,
        headers: {
          ...(init?.headers ?? {}),
          Authorization: `Bearer pb:${currentToken}`,
        },
      });
      if (res.status === 401) {
        // Token expired — refresh once via loadManifest.
        const refreshed = await loadManifest(activeShareId, API_BASE, manifestGuestAccess);
        if (refreshed.state === 'ok' && refreshed.playbackToken) {
          currentToken = refreshed.playbackToken;
          onTokenRefresh(currentToken);
          // Retry with new token.
          return fetchImpl(url, {
            ...init,
            headers: {
              ...(init?.headers ?? {}),
              Authorization: `Bearer pb:${currentToken}`,
            },
          });
        }
        // Still unauthorized — show login gate and abort the current mount.
        if (refreshed.state === 'needs-auth') {
          mountLoginGate(appHost, {
            teamName: refreshed.teamName,
            apiBase: API_BASE,
            // This is a mid-listen re-auth (401 after a failed refresh), not a
            // first visit — tell the user their session expired rather than
            // showing the generic gate copy.
            sessionExpired: true,
            onAuthed: () => {
              // Reload the page to re-run boot with fresh cookie.
              if (typeof location !== 'undefined') location.reload();
            },
          });
          // Throw a sentinel so mountPlayer's catch does NOT render the error
          // screen on top of the gate we just mounted.
          throw new GateShownError();
        }
        // Access to the team was lost mid-listen (removed from it, or signed in
        // as another account): the same not-a-member screen boot shows, instead
        // of handing the stem loader a raw 401 (review of #421).
        if (refreshed.state === 'not-a-member') {
          renderFatalError({ kind: 'not-a-member', teamName: refreshed.teamName });
          throw new GateShownError();
        }
      }
      return res;
    };
  };

  // ── Shell vs. per-song split (B-260622) ───────────────────────────────────
  // The app shell — setlist rail, topbar, promo footer, modals, the mobile
  // drawer backdrop + its body-class wiring, and the player-grid body flags —
  // is mounted ONCE per boot. Only the per-song player region (player-view,
  // section handlers, audio graph) is rebuilt on a song switch. Tearing down
  // and re-mounting the rail on every switch made the whole UI flash, including
  // the row the user just clicked.
  //
  // `disposeShell` tears the shell down (final boot unmount only). `disposeSong`
  // tears down JUST the current song region and is what `loadSong` calls before
  // swapping in the next song. The rail's active-song highlight is NOT driven by
  // re-mounting — it flows through the rail's own `subscribe`→`applyActive`
  // reactive path off `state.activeShareId` (setlist-rail.ts).
  let disposeShell: (() => void) | null = null;
  let disposeSong: (() => void) | null = null;

  // Monotonic song-switch counter. loadSong() captures its value; if a newer
  // switch begins while an older one awaits its manifest, the stale one bails
  // instead of mounting a second player over the current one. (web review P2)
  let loadGen = 0;

  // Tear the player fully down, then render a full-page error. renderErrorScreen
  // wipes #app, so calling it without disposing first LEAKS the shell's rAF loop
  // (60 fps setState forever), its drawer backdrop (appended to <body>), and its
  // subscriptions — and a later loadSong() would crash on the wiped mount points.
  // (web review P2)
  const renderFatalError = (error: { kind: string; teamName?: string | null }, onRetry?: () => void): void => {
    disposeSong?.();
    disposeSong = null;
    disposeShell?.();
    disposeShell = null;
    setState({ error });
    renderErrorScreen(appHost, error, onRetry);
  };

  // Mount the persistent shell once. Receives the first manifest so the
  // single-share case can still synthesize a one-song rail (or fall back to the
  // promo sidebar) exactly as before.
  const mountShell = (manifest: any): void => {
    // Real setlist-rail when we have setlist context — synthesize a single-song
    // list from the manifest when there's no setlist context — so the promo
    // sidebar (with its "Get app" CTA) never appears in the member player.
    // D3: songs[] present → wiring owns the rail (all songs, version choice,
    // words-only). Otherwise → the same synthesized-single-song fallback as before.
    let sidebar: { unmount: () => void };
    if (wiring && wiring.kind === 'songs') {
      sidebar = wiring.mountRail(byId('share-rail'));
    } else {
      const railItems = getState().setlistItems;
      const effectiveItems = railItems.length > 0
        ? railItems
        : [{ position: 0, shareId: activeShareId, songId: null, songName: manifest.song?.name ?? null }];
      // effectiveItems is always non-empty (real setlist items, or a
      // synthesized single-song entry), so the rail is always the
      // setlist-rail — the promo sidebar fallback was unreachable and has
      // been dropped.
      sidebar = mountSetlistRail(byId('share-rail'), {
        items: effectiveItems,
        activeShareId,
        name: getState().setlistName,
        createdAt: getState().setlistCreatedAt,
        onSelect: (nextShareId) => { void loadSong(nextShareId); },
      });
    }

    // 53-я drawer backdrop (mobile only — toggles body.rail-open class to lock scroll).
    const backdrop = document.createElement('div');
    backdrop.className = 'rail-backdrop';
    backdrop.addEventListener('click', () => setState({ sidebarOpen: false }));
    document.body.appendChild(backdrop);

    const railEl = byId('share-rail');
    const unsubBackdrop = subscribe((s) => {
      // `body.rail-open` shows the backdrop + locks scroll; `#share-rail.is-open`
      // actually slides the drawer in (CSS translateX). The new setlist-rail
      // (unlike the legacy sidebar) doesn't self-toggle is-open, so do it here —
      // otherwise the burger only darkened the screen with no rail (B-104 sibling).
      document.body.classList.toggle('rail-open', s.sidebarOpen);
      railEl.classList.toggle('is-open', s.sidebarOpen);
    });

    const topbar = mountAppHeader(byId('share-topbar'), {
      apiBase: API_BASE,
      navigate,
      onToggleRail: () => setState({ sidebarOpen: !getState().sidebarOpen }),
      onReport: () => openFeedbackModal(),
      // Выход отсюда обязан стирать кэш так же, как из библиотеки: меню
      // аккаунта одно на все маршруты, и «стирает только с той страницы»
      // было бы правилом, о котором человек не догадается.
      eraseOffline: eraseOfflineData,
    });
    // Playback layout hides the takes/versions rail for now and reclaims its
    // grid track so the player panel gets the width.
    // Flag the body so player-view.css can hide promo chrome (download CTA,
    // attribution footer) that doesn't belong in the member player.
    if (document.body) document.body.setAttribute('data-pv', 'true');
    const takesHost = byId('share-takes-sidebar') as HTMLElement;
    takesHost.style.display = 'none';
    const rv = document.getElementById('share-rehearsal-view') as HTMLElement | null;
    if (rv) rv.style.gridTemplateColumns = '1fr';
    const footer = mountPromoFooter(byId('share-promo-footer'));
    const promoModal = mountPromoModal(byId('share-promo-modal-host'));
    const shareModal = mountShareModal(byId('share-share-modal-host'));

    disposeShell = () => {
      unsubBackdrop();
      backdrop.remove();
      document.body.classList.remove('rail-open');
      topbar.unmount();
      sidebar.unmount();
      footer.unmount();
      promoModal.unmount();
      shareModal.unmount();
    };
  };

  // Build (or rebuild) ONLY the per-song region: gated peaks fetch, audio graph,
  // player-view, section gestures. Called for the first song at boot and for
  // every subsequent switch from loadSong(). The AudioContext is intentionally
  // created fresh per song (and closed by loadSong) — unchanged this pass.
  const swapSong = async (manifest: any, playbackToken: string | null, gen?: number): Promise<void> => {
    let currentToken = playbackToken;
    // Pin the shareId for THIS song so its peaks/audio URLs stay correct even if
    // a concurrent loadSong() advances the mutable activeShareId mid-fetch. (P2)
    const songShareId = activeShareId;

    // Peaks: if we have a token, fetch through worker; otherwise use legacy CDN.
    let peaks: SummaryPeaks;
    let perTrackPeaks: PerTrackSummaryPeaks | null;
    try {
      if (currentToken) {
        // Fetch peaks through the worker gate with pb token.
        const gatedFetch = makeGatedFetch(currentToken, (t) => { currentToken = t; });
        const peaksUrl = manifest.peaksUrl ?? 'peaks.json';
        const peaksRes = await gatedFetch(`${API_BASE}/api/shares/${songShareId}/${peaksUrl}`);
        if (!peaksRes.ok) throw new Error(`peaks fetch failed: ${peaksRes.status}`);
        peaks = await peaksRes.json();
        // Per-track peaks.
        if (manifest.v >= 2 && manifest.perTrackPeaksUrl) {
          try {
            const ptRes = await gatedFetch(`${API_BASE}/api/shares/${songShareId}/${manifest.perTrackPeaksUrl}`);
            perTrackPeaks = ptRes.ok ? await ptRes.json() : null;
          } catch { perTrackPeaks = null; }
        } else {
          perTrackPeaks = null;
        }
      } else {
        // Legacy CDN path (no token — tests/public bundles without D1 row).
        [peaks, perTrackPeaks] = await Promise.all([
          loadPeaks(cdnBase, songShareId, manifest.peaksUrl),
          loadPerTrackPeaks(cdnBase, songShareId, manifest),
        ]);
      }
    } catch (err) {
      // GateShownError means the login gate is already mounted — do not
      // render the error screen on top of it.
      if (err instanceof GateShownError) return;
      const message = err instanceof Error ? err.message : String(err);
      const error = {
        kind: isNotFoundError(message) ? 'not-found' : 'load-failed',
        message,
      };
      renderFatalError(error);
      return;
    }

    // A newer song switch superseded this one while peaks were fetching — bail
    // before mounting so we don't overwrite the current disposeSong (leaking its
    // player + rAF) or mount a second player. (web review P2)
    if (gen !== undefined && gen !== loadGen) return;

    setState({ manifest, peaks });

    // Injected factory (tests) wins verbatim; production falls back to the
    // 24 kHz playback context (Component 1).
    const audioContext = (opts.audioContextFactory ?? createPlaybackContext)();
    currentAudioContext = audioContext;

    const player: Player = createPlayer({
      audioContext,
      manifest,
      fetchAudio: async (url, signal) => {
        // audit 5.2 — forward the player's AbortSignal into the real fetch so a
        // song switch (disposeSong → player.dispose() → abort()) cancels this
        // song's in-flight stem downloads. makeGatedFetch spreads `init` (incl.
        // its 401-refresh retry), so the signal reaches every underlying fetch.
        if (currentToken) {
          // Route through worker with pb token.
          const gatedFetch = makeGatedFetch(currentToken, (t) => { currentToken = t; });
          const res = await gatedFetch(`${API_BASE}/api/shares/${songShareId}/${url}`, { signal });
          if (!res.ok) throw new Error(`audio fetch failed: ${res.status}`);
          return res.arrayBuffer();
        }
        // Legacy CDN path (no token).
        const fullUrl = `${cdnBase}/shares/${songShareId}/${url}`;
        const res = await fetchImpl(fullUrl, { signal });
        if (!res.ok) throw new Error(`audio fetch failed: ${res.status}`);
        return res.arrayBuffer();
      },
    });

    // Mirror player events into shared state so every panel reacts via the
    // same subscription mechanism.
    const unsubPlayer = player.subscribe((event) => {
      if (event.type === 'loaded') {
        setState({
          loaded: true,
          trackStates: new Map(player.getTrackStates()),
        });
      } else if (event.type === 'playing') {
        setState({ playing: true });
      } else if (event.type === 'paused') {
        setState({ playing: false });
      } else if (event.type === 'ended') {
        setState({ playing: false, playhead: 0 });
      }
    });

    // Periodic playhead sync so timeline/transport re-render from state.
    let rafHandle: number | null = null;
    const rafImpl: typeof requestAnimationFrame =
      typeof requestAnimationFrame === 'function'
        ? requestAnimationFrame.bind(globalThis)
        : ((cb: FrameRequestCallback) =>
            setTimeout(() => cb(Date.now()), 16) as unknown as number);
    const cancelRafImpl: typeof cancelAnimationFrame =
      typeof cancelAnimationFrame === 'function'
        ? cancelAnimationFrame.bind(globalThis)
        : ((id: number) => clearTimeout(id as unknown as ReturnType<typeof setTimeout>));

    const tickPlayhead = (): void => {
      setState({ playhead: player.getPlayhead() });
      rafHandle = rafImpl(tickPlayhead);
    };
    rafHandle = rafImpl(tickPlayhead);

    // Mobile background audio (summed-stream model): on a touch phone/tablet the
    // background <audio> becomes the sole audible source from the first Play tap
    // — the mix is pre-rendered to it, the live engine runs muted for visuals,
    // and Media Session drives the lock screen. Created BEFORE the player view so
    // its onPlayGesture() can be wired into the Play control (it must fire
    // synchronously inside the tap — the only moment iOS permits <audio>.play()).
    // On desktop the live engine stays primary and this is thin Media Session.
    const isMobile = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
    const bg = wireBackgroundPlayback({
      player,
      backgroundAudio: createBackgroundAudio(),
      renderToBlob: renderMixToBlob,
      wireMediaSession,
      setPositionState,
      setPlaybackState,
      sampleRate: WEB_PLAYBACK_SAMPLE_RATE,
      metadata: { title: manifest.song?.name ?? 'Band Way' },
      isMobile,
    });

    // Mount the per-song player region. The DS player (player-view) is the only
    // player path now. The shell (topbar/rail/footer/modals) is already mounted.
    const playerPanel = mountPlayerView(byId('share-player-panel'), {
      manifest, peaks, player, perTrackPeaks, audioContext,
      onNavigate: (id) => { void loadSong(id); },
      onPlayGesture: bg.onPlayGesture,
      // D3: «Version ▾» for this song — undefined outside the songs[] path.
      mountVersionActions: wiring && wiring.kind === 'songs' ? wiring.mountVersionActionsFor(songShareId) : undefined,
    });

    // Section gestures + loop wiring live inside player-view (waves.ts mounts
    // them on the `.pv-overview-band` it builds and keeps loop state local to
    // that closure). There is no `.rh-overview-band` in the share-web DOM, so
    // the former main-side gesture block here was dead and has been removed.

    // Kick off audio loading.
    player.load().catch((err) => {
      // Сессия протухла посреди загрузки стемов: gatedFetch уже показал вход и
      // бросил GateShownError — экран ошибки поверх входа не рисуем, как и в
      // остальных catch этого файла (B-260927-share-web-gate-wiped-on-load-401).
      if (err instanceof GateShownError) return;
      const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;
      const error = { kind: isOffline ? 'offline' : 'load-failed' };
      renderFatalError(error, () => { if (typeof location !== 'undefined') location.reload(); });
    });

    appHost.setAttribute('data-ready', 'true');

    // Tear down ONLY the per-song region — the shell stays mounted.
    disposeSong = () => {
      // audit 5.2 — abort the outgoing song's in-flight stem downloads first so
      // they don't waste bandwidth or race the incoming song. This is the SAME
      // teardown loadSong() already runs before advancing; no second mechanism.
      player.dispose();
      // Mobile background audio: remove the visibilitychange listener, clear the
      // orchestrator's timers, and tear down the hidden <audio> element.
      bg.teardown();
      if (rafHandle !== null) {
        cancelRafImpl(rafHandle);
        rafHandle = null;
      }
      unsubPlayer();
      playerPanel.unmount();
    };
  };

  // Mount the shell (once) + the first song. Used by the boot sequence and the
  // needs-auth retry path. `mountShell` is guarded so a post-auth retry doesn't
  // double-mount the rail/topbar.
  const mountPlayer = async (manifest: any, playbackToken: string | null): Promise<void> => {
    if (!disposeShell) mountShell(manifest);
    await swapSong(manifest, playbackToken);
  };

  // SP3: re-callable song switch — teardown current SONG → fetch+swap next.
  // B-260622: only the per-song region is torn down/rebuilt. The shell (rail,
  // topbar, footer, modals) stays mounted, so the rail the user just clicked
  // does not flash — its highlight moves via the reactive activeShareId path.
  async function loadSong(nextShareId: string): Promise<void> {
    // Claim this switch's generation. If another loadSong() starts while we await
    // the manifest below, loadGen advances and we bail before swapping. (P2)
    const myGen = ++loadGen;
    // 1. Teardown the current SONG region only (stops rAF, unsubscribes player,
    //    removes the player-panel DOM) — the shell is left intact…
    disposeSong?.();
    disposeSong = null;
    // …and dispose its AudioContext so audio graph nodes are released and no
    // two contexts ever play at once (design-spec risk: overlapping audio).
    if (currentAudioContext) {
      try { await currentAudioContext.close(); } catch { /* already closed */ }
      currentAudioContext = null;
    }
    // 2. Update the address bar without a reload, preserving ?setlist & ?pv —
    //    BEFORE the state write below: the songs rail rebuilds on exactly that
    //    write and reads `song` off `location.search` right then.
    if (typeof history !== 'undefined' && typeof history.pushState === 'function'
        && typeof location !== 'undefined') {
      // D3: drop a stale `&song=<n>` (words-only row marker) — this is a
      // real song now (setlist-player-wiring.ts reads `song` off `location.search`).
      const url = new URL(location.href);
      url.searchParams.delete('song');
      history.pushState({ shareId: nextShareId }, '', `/${nextShareId}${url.search}`);
    }
    // 3. Advance the active share (drives gated fetch URLs + rail highlight).
    activeShareId = nextShareId;
    setState({ activeShareId: nextShareId });
    // 4. Fetch the new manifest (gated) and rebuild ONLY the player region.
    const next = await fetchManifestGated();
    // A newer switch superseded this one during the fetch — stop here so we don't
    // mount a stale song over the current one. (P2)
    if (myGen !== loadGen) return;
    if (next) await swapSong(next.manifest, next.playbackToken, myGen);
  }

  // SP3: if entered from a published setlist (`?setlist=:id`), load the ordered
  // items up front so mountPlayer mounts the real rail (not the promo sidebar).
  const rawSetlistParam = typeof location !== 'undefined'
    ? new URLSearchParams(location.search).get('setlist')
    : null;
  // Validate the query-string id before it reaches the API path (mirrors the
  // pathname route's SETLIST_PATH_RE). Without this, ?setlist=../<endpoint> would
  // be normalized by fetch into a cookie-authed GET on an arbitrary same-origin
  // /api path. (web review P3)
  const setlistParam = rawSetlistParam && /^[a-zA-Z0-9-]{1,64}$/.test(rawSetlistParam)
    ? rawSetlistParam
    : null;
  if (setlistParam) {
    wiring = await wireSetlistPlayer({
      setlistId: setlistParam,
      routeShareId: activeShareId,
      guestKey: manifestGuestKey,
      userId: readOfflineUserId(),
      apiBase: API_BASE,
      fetchImpl,
      deps: {
        fetchSetlist: fetchPublishedSetlist,
        fetchSongText,
        storage: typeof localStorage !== 'undefined' ? localStorage : { getItem: () => null, setItem: () => {} },
        navigate: (nextShareId) => { void loadSong(nextShareId); },
        teardownSong: async () => {
          disposeSong?.();
          disposeSong = null;
          if (currentAudioContext) {
            try { await currentAudioContext.close(); } catch { /* already closed */ }
            currentAudioContext = null;
          }
        },
        setSongTeardown: (fn) => { disposeSong = fn; },
        getPlayerPanelHost: () => byId('share-player-panel'),
      },
    });
    if (wiring.kind !== 'none') setState({ setlistItems: wiring.items, setlistName: wiring.name, setlistCreatedAt: wiring.createdAt });
  }
  setState({ activeShareId });

  // SP3: browser back/forward re-loads the matching song in place. The handler
  // is registered once per boot and removed by the returned unmount().
  const onPopState = (): void => {
    if (typeof location === 'undefined') return;
    const popped = parseShareIdFromPath(location.pathname);
    if (popped && popped !== activeShareId) void loadSong(popped);
  };
  if (typeof window !== 'undefined') window.addEventListener('popstate', onPopState);

  // iOS: keep audio audible even when the hardware silent switch is on (Web
  // Audio is otherwise muted by it). Installs a one-shot gesture listener.
  const stopIosAudio = enableIosSilentPlayback();

  // ── Boot sequence ─────────────────────────────────────────────────────────
  const manifestData = await fetchManifestGated();
  if (!manifestData) {
    // Gate displayed (needs-auth, revoked, or error screen) — return a
    // synthetic error result so callers can detect boot did not complete.
    const currentError = { kind: 'gate-displayed', message: 'auth gate shown or error rendered' };
    return { error: currentError };
  }

  await mountPlayer(manifestData.manifest, manifestData.playbackToken);
  // Full teardown: drop the popstate listener (registered above) + iOS hook,
  // then tear down the current song region AND the persistent shell.
  return {
    unmount: () => {
      if (typeof window !== 'undefined') window.removeEventListener('popstate', onPopState);
      stopIosAudio();
      disposeSong?.();
      disposeShell?.();
    },
    loadSong,
  };
}

// `getState` is re-exported only to silence the unused-import linter; the
// orchestrator itself doesn't call it (state flows in one direction here).
// Keeping the import live lets future event handlers reach for the current
// snapshot without re-wiring the module graph.
void getState;

// Auto-boot on page load. Tests set the marker before importing the module
// so `boot()` can be driven explicitly with mocks.
if (!(globalThis as { __SHARE_WEB_TEST__?: boolean }).__SHARE_WEB_TEST__) {
  boot().catch((err) => {
    // eslint-disable-next-line no-console
    console.error('[share-web] boot failed:', err);
  });
}
